import * as React from 'react';
import {
    Panel, PanelType, Stack, Text, ComboBox, IComboBoxOption, IComboBox,
    PrimaryButton, DefaultButton, MessageBar, MessageBarType, MessageBarButton,
    Spinner, SpinnerSize, mergeStyles, Separator, IconButton, ActionButton,
    Dialog, DialogType, DialogFooter, TextField, Checkbox
} from '@fluentui/react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import {
    getOrphanCustomers, adoptOrphanCustomer, getAllTenantsLite,
    dismissOrphanCustomer, dismissUnresolvedEvents, undismissOrphanEvents
} from '../../utilities/helpers/ApiHelper';

interface IOrphanSubscriptionItem {
    productId: string;
    description: string;
    amount: number;
    quantity: number;
}

interface IOrphanSubscription {
    id: string;
    status: string;
    currency: string;
    totalAmount: number;
    items: IOrphanSubscriptionItem[];
}

interface IOrphanEvent {
    id: number;
    stripeEventId: string;
    eventType: string;
    objectId: string;
    receivedAt: string;
    lastError: string;
}

export interface IOrphanCustomer {
    customerId: string;
    customerEmail: string;
    customerName: string;
    customerCountry: string;
    hasTenantGuidOnCustomer: boolean;
    deletedCustomer: boolean;
    events: IOrphanEvent[];
    subscriptions: IOrphanSubscription[];
}

interface IOrphansResponse {
    orphans: IOrphanCustomer[];
    truncated: boolean;
    unresolvedEvents: IOrphanEvent[];
}

interface ITenantLite {
    id: number;
    tenantId: string;
    friendlyName: string;
}

interface Props {
    isOpen: boolean;
    onDismiss: () => void;
    // Called after every successful adoption / dismissal / undo so the parent (Subscriptions
    // view) can refresh its toolbar badge / banner without waiting for the panel to close.
    onCountChanged?: () => void;
}

// What the shared reason dialog is about to dismiss. Customer dismissals resolve their ids on
// the server (under the per-customer lock); unresolved dismissals send the ticked ids.
type DismissTarget =
    | { kind: 'customer'; customerId: string }
    | { kind: 'unresolved'; ids: number[] };

const MAX_REASON_LENGTH = 500;
const MESSAGE_TTL_MS = 6000;

const sectionLabel = mergeStyles({
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
});

const monoStyle = mergeStyles({
    fontFamily: 'Consolas, monospace',
    fontSize: 12,
    color: "var(--db-fg)",
});

const mutedStyle = mergeStyles({
    color: "var(--db-fg-muted)",
});

const rowCardStyle = mergeStyles({
    border: `1px solid var(--db-border)`,
    borderRadius: 4,
    marginBottom: 8,
    backgroundColor: "var(--db-surface-raised)",
});

const rowHeaderStyle = mergeStyles({
    padding: '10px 12px',
    cursor: 'pointer',
    selectors: {
        ':hover': { backgroundColor: "var(--db-hover-bg)" },
    },
});

const rowBodyStyle = mergeStyles({
    padding: '0 12px 12px 12px',
    borderTop: `1px solid var(--db-border)`,
});

const unresolvedRowStyle = mergeStyles({
    padding: '6px 0',
    borderBottom: `1px solid var(--db-border)`,
});

function formatAmount(minor: number, currency: string): string {
    if (!minor && minor !== 0) return '—';
    const major = minor / 100;
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'EUR').toUpperCase() }).format(major);
    } catch {
        return `${(currency || '').toUpperCase()} ${major.toFixed(2)}`;
    }
}

function shortGuid(g: string): string {
    if (!g) return '';
    return g.length > 8 ? g.substring(0, 8) : g;
}

function truncate(s: string, max: number): string {
    if (!s) return '';
    return s.length > max ? `${s.substring(0, max)}…` : s;
}

// ─── Reason dialog ────────────────────────────────────────────────────────────

interface DismissDialogProps {
    hidden: boolean;
    busy: boolean;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

const DismissDialog: React.FC<DismissDialogProps> = ({ hidden, busy, onConfirm, onCancel }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [reason, setReason] = React.useState<string>('');
    const trimmed = reason.trim();

    // A fresh dialog per target: never carry a reason from one dismissal into the next.
    React.useEffect(() => {
        if (hidden) setReason('');
    }, [hidden]);

    return (
        <Dialog
            hidden={hidden}
            onDismiss={busy ? undefined : onCancel}
            dialogContentProps={{
                type: DialogType.normal,
                title: languageStrings.DismissOrphanTitle,
                subText: languageStrings.DismissOrphanSubText,
            }}
            modalProps={{ isBlocking: true }}
            minWidth={420}
        >
            <TextField
                label={languageStrings.DismissOrphanReason}
                placeholder={languageStrings.DismissOrphanReasonPlaceholder}
                multiline
                rows={3}
                maxLength={MAX_REASON_LENGTH}
                value={reason}
                onChange={(_: React.FormEvent, v?: string) => setReason(v ?? '')}
                disabled={busy}
            />
            <DialogFooter>
                <PrimaryButton
                    text={busy ? '' : languageStrings.DismissOrphan}
                    disabled={!trimmed || busy}
                    onClick={() => onConfirm(trimmed)}
                    onRenderIcon={busy ? () => <Spinner size={SpinnerSize.small} /> : undefined}
                />
                <DefaultButton text={languageStrings.Cancel} onClick={onCancel} disabled={busy} />
            </DialogFooter>
        </Dialog>
    );
};

// ─── Row sub-component ────────────────────────────────────────────────────────

interface RowProps {
    orphan: IOrphanCustomer;
    tenantOptions: IComboBoxOption[];
    tenantsLoading: boolean;
    actionsBusy: boolean;
    onAdopted: (customerId: string) => void;
    onRequestDismiss: (customerId: string) => void;
}

const OrphanRow: React.FC<RowProps> = ({ orphan, tenantOptions, tenantsLoading, actionsBusy, onAdopted, onRequestDismiss }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [selectedTenantId, setSelectedTenantId] = React.useState<number | null>(null);
    const [isAdopting, setIsAdopting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleAdopt = async () => {
        if (!selectedTenantId) return;
        setIsAdopting(true);
        setError(null);
        try {
            const result = await adoptOrphanCustomer(orphan.customerId, selectedTenantId);
            if (result && result.customerId) {
                onAdopted(orphan.customerId);
            } else {
                setError('Adoption failed. The customer or one of its subscriptions could not be updated in Stripe.');
            }
        } catch (e) {
            setError((e as Error)?.message || 'Adoption failed.');
        } finally {
            setIsAdopting(false);
        }
    };

    const totals = orphan.subscriptions.reduce((acc, s) => {
        const cur = (s.currency || 'eur').toLowerCase();
        acc[cur] = (acc[cur] || 0) + (s.totalAmount || 0);
        return acc;
    }, {} as Record<string, number>);
    const totalText = Object.keys(totals).length === 0
        ? '—'
        : Object.keys(totals).map(c => formatAmount(totals[c], c)).join(' / ');

    const adoptBlocked = isAdopting || actionsBusy || orphan.deletedCustomer;

    return (
        <div className={rowCardStyle}>
            <div
                className={rowHeaderStyle}
                onClick={() => setExpanded(!expanded)}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpanded(!expanded); } }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <IconButton
                        iconProps={{ iconName: expanded ? 'ChevronDown' : 'ChevronRight' }}
                        ariaLabel={expanded ? 'Collapse' : 'Expand'}
                        styles={{ root: { height: 24, width: 24 } }}
                    />
                    <Stack grow>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                                {orphan.customerEmail || '—'}
                            </Text>
                            {orphan.customerCountry ? (
                                <Text variant="small" className={mutedStyle}>
                                    · {orphan.customerCountry}
                                </Text>
                            ) : null}
                            {orphan.customerName ? (
                                <Text variant="small" className={mutedStyle}>
                                    · {orphan.customerName}
                                </Text>
                            ) : null}
                            {orphan.deletedCustomer ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-error-fg)", fontWeight: 600 } }}>
                                    · {languageStrings.CustomerDeleted}
                                </Text>
                            ) : null}
                            {orphan.hasTenantGuidOnCustomer ? (
                                <Text variant="small" styles={{ root: { color: "var(--db-success-fg)", fontWeight: 600 } }}>
                                    · {languageStrings.HasTenantGuidStamped}
                                </Text>
                            ) : null}
                        </Stack>
                        <Text variant="small" className={monoStyle}>
                            {orphan.customerId} · {orphan.events.length} {languageStrings.StuckEvents} · {orphan.subscriptions.length} {languageStrings.StuckSubscriptions} · {totalText}
                        </Text>
                    </Stack>
                </Stack>
            </div>

            {expanded ? (
                <div className={rowBodyStyle}>
                    <Text className={sectionLabel}>{languageStrings.StuckSubscriptions}</Text>
                    {orphan.subscriptions.length === 0 ? (
                        <Text className={mutedStyle}>—</Text>
                    ) : (
                        orphan.subscriptions.map(sub => (
                            <Stack key={sub.id} tokens={{ childrenGap: 2 }} styles={{ root: { marginBottom: 8, paddingLeft: 8, borderLeft: `2px solid var(--db-border)` } }}>
                                <Text className={monoStyle}>{sub.id} · {sub.status} · {formatAmount(sub.totalAmount, sub.currency)}</Text>
                                {sub.items.map((it, idx) => (
                                    <Text key={idx} variant="small" className={mutedStyle}>
                                        {it.quantity}× {it.description || it.productId || '(item)'} — {formatAmount(it.amount, sub.currency)}
                                    </Text>
                                ))}
                            </Stack>
                        ))
                    )}

                    <Text className={sectionLabel}>{languageStrings.StuckEvents}</Text>
                    <Stack tokens={{ childrenGap: 2 }}>
                        {orphan.events.map(ev => (
                            <Text key={ev.id} variant="small" className={monoStyle}>
                                {ev.eventType} · {new Date(ev.receivedAt).toLocaleString()}
                            </Text>
                        ))}
                    </Stack>

                    <Separator />

                    {orphan.deletedCustomer ? (
                        <MessageBar messageBarType={MessageBarType.info} isMultiline={true} styles={{ root: { marginBottom: 8 } }}>
                            {languageStrings.CustomerDeletedHint}
                        </MessageBar>
                    ) : null}

                    <Text className={sectionLabel}>{languageStrings.PickTenant}</Text>
                    {tenantsLoading ? (
                        <Spinner size={SpinnerSize.small} label={languageStrings.Loading} />
                    ) : (
                        <ComboBox
                            placeholder={languageStrings.PickTenantPlaceholder}
                            options={tenantOptions}
                            selectedKey={selectedTenantId ?? undefined}
                            allowFreeform={false}
                            autoComplete="on"
                            useComboBoxAsMenuWidth
                            onChange={(_: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
                                if (option) setSelectedTenantId(option.key as number);
                            }}
                            disabled={adoptBlocked}
                        />
                    )}

                    <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 12 } }}>
                        <PrimaryButton
                            text={isAdopting ? '' : languageStrings.AdoptAndReprocess}
                            disabled={!selectedTenantId || adoptBlocked}
                            onClick={handleAdopt}
                            onRenderIcon={isAdopting ? () => <Spinner size={SpinnerSize.small} /> : undefined}
                        />
                        <DefaultButton
                            text={languageStrings.DismissOrphan}
                            disabled={isAdopting || actionsBusy}
                            onClick={() => onRequestDismiss(orphan.customerId)}
                        />
                    </Stack>

                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={true} styles={{ root: { marginTop: 8 } }}>
                        {languageStrings.AdoptOrphanWarning}
                    </MessageBar>

                    {error ? (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} isMultiline={true} styles={{ root: { marginTop: 8 } }}>
                            {error}
                        </MessageBar>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ─── Panel ────────────────────────────────────────────────────────────────────

const OrphanCustomersPanel: React.FC<Props> = ({ isOpen, onDismiss, onCountChanged }) => {
    const languageStrings = React.useContext(LanguageContext);
    const [data, setData] = React.useState<IOrphansResponse | null>(null);
    const [tenants, setTenants] = React.useState<ITenantLite[]>([]);
    const [tenantsLoading, setTenantsLoading] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    // Dismiss / undo state.
    const [dismissTarget, setDismissTarget] = React.useState<DismissTarget | null>(null);
    const [isDismissing, setIsDismissing] = React.useState<boolean>(false);
    const [isUndoing, setIsUndoing] = React.useState<boolean>(false);
    // Ids the last dismissal flipped; drives the Undo button on the success bar.
    const [undoIds, setUndoIds] = React.useState<number[] | null>(null);
    const [selectedUnresolved, setSelectedUnresolved] = React.useState<Set<number>>(new Set());

    const messageTimerRef = React.useRef<number | null>(null);
    const clearMessageTimer = React.useCallback(() => {
        if (messageTimerRef.current !== null) {
            window.clearTimeout(messageTimerRef.current);
            messageTimerRef.current = null;
        }
    }, []);
    // Success bars (and the Undo they carry) expire after a short window.
    const showTransientSuccess = React.useCallback((message: string, ids: number[] | null) => {
        clearMessageTimer();
        setSuccessMessage(message);
        setUndoIds(ids && ids.length > 0 ? ids : null);
        messageTimerRef.current = window.setTimeout(() => {
            setSuccessMessage(null);
            setUndoIds(null);
            messageTimerRef.current = null;
        }, MESSAGE_TTL_MS);
    }, [clearMessageTimer]);
    React.useEffect(() => clearMessageTimer, [clearMessageTimer]);

    const fetchOrphans = React.useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result: IOrphansResponse = await getOrphanCustomers();
            if (result) {
                setData(result);
            } else {
                setError('Failed to load orphan customers.');
            }
        } catch (e) {
            setError((e as Error)?.message || 'Failed to load orphan customers.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isOpen) {
            clearMessageTimer();
            setSuccessMessage(null);
            setUndoIds(null);
            setError(null);
            setDismissTarget(null);
            setSelectedUnresolved(new Set());
            return;
        }
        fetchOrphans();
        // Tenants don't change between adoptions; load once per panel-open.
        let cancelled = false;
        (async () => {
            setTenantsLoading(true);
            try {
                const result: ITenantLite[] = await getAllTenantsLite();
                if (!cancelled && Array.isArray(result)) {
                    setTenants(result);
                }
            } finally {
                if (!cancelled) setTenantsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, fetchOrphans, clearMessageTimer]);

    // Drop ticks for unresolved events that vanished on a refresh (dismissed elsewhere, replayed,
    // or purged) so a later "dismiss selected" can never send ids the operator is not looking at.
    React.useEffect(() => {
        const present = new Set((data?.unresolvedEvents || []).map(e => e.id));
        setSelectedUnresolved(prev => {
            const next = new Set(Array.from(prev).filter(id => present.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [data]);

    const tenantOptions: IComboBoxOption[] = React.useMemo(() => {
        return tenants.map(t => ({
            key: t.id,
            text: `${t.friendlyName || '(unnamed)'}  ·  ${shortGuid(t.tenantId)}`,
            data: t,
        }));
    }, [tenants]);

    const handleAdopted = (_customerId: string) => {
        showTransientSuccess(languageStrings.OrphanAdopted, null);
        if (onCountChanged) onCountChanged();
        // Refresh the list so the adopted row disappears.
        fetchOrphans();
    };

    const handleConfirmDismiss = async (reason: string) => {
        const target = dismissTarget;
        if (!target) return;
        setIsDismissing(true);
        setError(null);
        try {
            const result = target.kind === 'customer'
                ? await dismissOrphanCustomer(target.customerId, reason)
                : await dismissUnresolvedEvents(target.ids, reason);
            if (!result) {
                throw new Error('Dismissal failed.');
            }
            showTransientSuccess(languageStrings.OrphanDismissed, result.dismissedIds || []);
            setSelectedUnresolved(new Set());
            if (onCountChanged) onCountChanged();
            setIsDismissing(false);
            setDismissTarget(null);
            fetchOrphans();
        } catch (e) {
            // Typically "nothing left to dismiss": another operator got there first. Refresh so
            // the stale row goes away, then show the server's reason (fetchOrphans clears the
            // error bar when it starts, so the message has to land after it).
            const message = (e as Error)?.message || 'Dismissal failed.';
            setIsDismissing(false);
            setDismissTarget(null);
            await fetchOrphans();
            setError(message);
        }
    };

    const handleUndo = async () => {
        const ids = undoIds;
        if (!ids || ids.length === 0 || isUndoing) return;
        clearMessageTimer();
        setUndoIds(null);
        setSuccessMessage(null);
        setError(null);
        setIsUndoing(true);
        try {
            const result = await undismissOrphanEvents(ids);
            if (!result) {
                throw new Error('Undo failed.');
            }
            showTransientSuccess(languageStrings.OrphanDismissalUndone, null);
            if (onCountChanged) onCountChanged();
            setIsUndoing(false);
            fetchOrphans();
        } catch (e) {
            const message = (e as Error)?.message || 'Undo failed.';
            setIsUndoing(false);
            await fetchOrphans();
            setError(message);
        }
    };

    const unresolved = data?.unresolvedEvents || [];
    const allUnresolvedSelected = unresolved.length > 0 && unresolved.every(e => selectedUnresolved.has(e.id));
    const someUnresolvedSelected = selectedUnresolved.size > 0 && !allUnresolvedSelected;
    const toggleAllUnresolved = () => {
        setSelectedUnresolved(allUnresolvedSelected ? new Set() : new Set(unresolved.map(e => e.id)));
    };
    const toggleUnresolved = (id: number) => {
        setSelectedUnresolved(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const actionsBusy = isDismissing || isUndoing;

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={`${languageStrings.OrphanCustomers}${data ? ` (${data.orphans.length})` : ''}`}
            type={PanelType.custom}
            customWidth="640px"
            isBlocking
            closeButtonAriaLabel={languageStrings.Cancel}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                <Text variant="small" className={mutedStyle}>
                    {languageStrings.OrphanCustomersDescription}
                </Text>

                <Stack horizontal horizontalAlign="end">
                    <ActionButton
                        iconProps={{ iconName: 'Refresh' }}
                        text={languageStrings.Refresh}
                        onClick={fetchOrphans}
                        disabled={isLoading}
                    />
                </Stack>

                {successMessage ? (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        onDismiss={() => { clearMessageTimer(); setSuccessMessage(null); setUndoIds(null); }}
                        isMultiline={false}
                        actions={undoIds ? (
                            <div>
                                <MessageBarButton onClick={handleUndo} disabled={isUndoing}>
                                    {languageStrings.UndoDismiss}
                                </MessageBarButton>
                            </div>
                        ) : undefined}
                    >
                        {successMessage}
                    </MessageBar>
                ) : null}

                {error ? (
                    <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)} isMultiline={true}>
                        {error}
                    </MessageBar>
                ) : null}

                {data?.truncated ? (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
                        {'More than 100 orphan customers found — only the first 100 are shown.'}
                    </MessageBar>
                ) : null}

                {isLoading ? (
                    <Stack horizontalAlign="center" tokens={{ padding: 30 }}>
                        <Spinner size={SpinnerSize.large} label={languageStrings.Loading} />
                    </Stack>
                ) : (
                    <>
                        {data && data.orphans.length === 0 ? (
                            <Stack horizontalAlign="center" tokens={{ padding: 30 }}>
                                <Text styles={{ root: { color: "var(--db-success-fg)", fontWeight: 600 } }}>
                                    {languageStrings.NoOrphans}
                                </Text>
                            </Stack>
                        ) : (
                            <Stack>
                                {(data?.orphans || []).map(o => (
                                    <OrphanRow
                                        key={o.customerId}
                                        orphan={o}
                                        tenantOptions={tenantOptions}
                                        tenantsLoading={tenantsLoading}
                                        actionsBusy={actionsBusy}
                                        onAdopted={handleAdopted}
                                        onRequestDismiss={(customerId) => setDismissTarget({ kind: 'customer', customerId })}
                                    />
                                ))}
                            </Stack>
                        )}

                        {unresolved.length > 0 ? (
                            <>
                                <Separator />
                                <Text className={sectionLabel}>
                                    {languageStrings.UnresolvedDeadLetteredEvents} ({unresolved.length})
                                </Text>
                                <Text variant="small" className={mutedStyle}>
                                    {languageStrings.UnresolvedEventsHint}
                                </Text>
                                <Checkbox
                                    label={languageStrings.SelectAllEvents}
                                    checked={allUnresolvedSelected}
                                    indeterminate={someUnresolvedSelected}
                                    onChange={toggleAllUnresolved}
                                    disabled={actionsBusy}
                                    styles={{ root: { marginTop: 8, marginBottom: 4 } }}
                                />
                                <Stack>
                                    {unresolved.map(ev => (
                                        <Stack key={ev.id} horizontal verticalAlign="start" tokens={{ childrenGap: 8 }} className={unresolvedRowStyle}>
                                            <Checkbox
                                                checked={selectedUnresolved.has(ev.id)}
                                                onChange={() => toggleUnresolved(ev.id)}
                                                ariaLabel={`${ev.eventType} · ${ev.objectId || ev.stripeEventId}`}
                                                disabled={actionsBusy}
                                            />
                                            <Stack tokens={{ childrenGap: 2 }}>
                                                <Text variant="small" className={monoStyle}>
                                                    {ev.eventType} · {ev.objectId || '—'} · {new Date(ev.receivedAt).toLocaleString()}
                                                </Text>
                                                {ev.lastError ? (
                                                    <Text variant="small" className={mutedStyle} title={ev.lastError}>
                                                        {truncate(ev.lastError, 160)}
                                                    </Text>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Stack horizontal styles={{ root: { marginTop: 12 } }}>
                                    <PrimaryButton
                                        text={`${languageStrings.DismissSelected} (${selectedUnresolved.size})`}
                                        disabled={selectedUnresolved.size === 0 || actionsBusy}
                                        onClick={() => setDismissTarget({ kind: 'unresolved', ids: Array.from(selectedUnresolved) })}
                                    />
                                </Stack>
                            </>
                        ) : null}
                    </>
                )}
            </Stack>

            <DismissDialog
                hidden={dismissTarget === null}
                busy={isDismissing}
                onConfirm={handleConfirmDismiss}
                onCancel={() => setDismissTarget(null)}
            />
        </Panel>
    );
};

export default OrphanCustomersPanel;
